/**
 * カメラの切り替え（docs/05_rendering-and-audio.md 10 章）。俯瞰（地図）と追従を 1 秒かけて補間する。
 * 俯瞰の操作（MapControls）は描画用とは別の代理カメラを動かし、描画用のカメラはその姿勢を写す。
 */
import * as THREE from 'three';
import { FOLLOW_FOV_DEG, type FollowCamera, type FollowTarget } from './followCamera';
import type { OverviewCamera } from './overviewCamera';

export type CameraMode = 'map' | 'follow';

const BLEND_S = 1;

export class CameraRig {
  mode: CameraMode = 'map';
  private blend = 1;
  private updated = false;
  private readonly fromPos = new THREE.Vector3();
  private readonly fromQuat = new THREE.Quaternion();
  private readonly aim = new THREE.PerspectiveCamera();

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    readonly map: OverviewCamera,
    private readonly mapCam: THREE.PerspectiveCamera,
    readonly follow: FollowCamera,
  ) {
    map.controls.enabled = true;
  }

  /** モードを変える。地図に戻るときは focus を画面の中心にする */
  setMode(mode: CameraMode, focus?: { x: number; z: number }): void {
    if (mode === this.mode) return;
    this.fromPos.copy(this.camera.position);
    this.fromQuat.copy(this.camera.quaternion);
    // まだ一度も描いていなければ補間しない（初期位置の原点から飛んでくるのを避ける）
    this.blend = this.updated ? 0 : 1;
    this.mode = mode;
    this.map.controls.enabled = mode === 'map';
    if (mode === 'map' && focus) this.map.focus(focus.x, focus.z);
    if (mode === 'follow') this.follow.reset();
  }

  update(realDt: number, target: FollowTarget | null): void {
    let pos: THREE.Vector3;
    let quat: THREE.Quaternion;
    if (this.mode === 'follow' && target) {
      const p = this.follow.update(target, realDt);
      this.aim.position.copy(p.position);
      this.aim.lookAt(p.look);
      pos = this.aim.position;
      quat = this.aim.quaternion;
    } else {
      this.map.update();
      pos = this.mapCam.position;
      quat = this.mapCam.quaternion;
    }
    this.blend = THREE.MathUtils.clamp(this.blend + realDt / BLEND_S, 0, 1);
    const k = this.blend * this.blend * (3 - 2 * this.blend);
    this.camera.position.lerpVectors(this.fromPos, pos, k);
    this.camera.quaternion.slerpQuaternions(this.fromQuat, quat, k);
    // 視野角：追従は広め、地図は代理カメラと同じ。切り替えの間はなめらかに
    const fovFrom = this.mode === 'follow' ? this.mapCam.fov : FOLLOW_FOV_DEG;
    const fovTo = this.mode === 'follow' ? FOLLOW_FOV_DEG : this.mapCam.fov;
    const fov = fovFrom + (fovTo - fovFrom) * k;
    if (Math.abs(this.camera.fov - fov) > 1e-3) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
    this.camera.updateMatrixWorld();
    this.updated = true;
  }
}
